import { useState, type ReactNode } from 'react';

import { useTranslation, type TFunction } from '@mint-lib/i18n';
import { CheckmarkOutline, Copy, WarningFilled } from '@mint-lib/icons';
import {
  Button,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  typographyVariants,
} from '@mint-lib/tw-components';
import { cn } from '@mint-lib/tw-utils';

type Props = {
  text: string;
  isMobile: boolean;
};

type CopyStatus = 'success' | 'idle' | 'error';
type CopyProps = {
  icon: ReactNode;
  text?: string;
};

const copyConfig = (t: TFunction) =>
  ({
    idle: {
      icon: <Copy />,
    },
    success: {
      icon: <CheckmarkOutline className="fill-success-main" />,
      text: t('Copied!'),
    },
    error: {
      icon: <WarningFilled className="fill-error-main" />,
      text: t('Copying error'),
    },
  }) satisfies Record<CopyStatus, CopyProps>;

const AiToolbar = ({ text, isMobile }: Props) => {
  const { t } = useTranslation('@mint-lib/ai-chat');
  const [copyStatus, setCopyStatus] = useState<CopyStatus>('idle');

  const onCopy = async (text: string): Promise<boolean> => {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // Safari/iOS fallback
      const textarea = Object.assign(document.createElement('textarea'), {
        value: text,
        style: { position: 'fixed', opacity: '0' },
      });
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      const success = document.execCommand('copy'); // NOSONAR - Safari/iOS fallback for clipboard copy
      textarea.remove();
      return success;
    }
  };

  const handleCopy = async () => {
    const success = await onCopy(text);
    setCopyStatus(success ? 'success' : 'error');
    setTimeout(() => setCopyStatus('idle'), 3000);
  };

  return (
    <div
      className={cn(
        'relative',
        isMobile
          ? 'flex h-8 items-center justify-start'
          : 'pointer-events-none h-6 opacity-0 transition-opacity group-hover:pointer-events-auto group-hover:opacity-100',
      )}
    >
      <Tooltip disableHoverableContent>
        <TooltipTrigger asChild>
          <Button
            aria-label="copy-tool"
            variant={'plain'}
            onClick={handleCopy}
            className="flex h-full flex-row items-center justify-center"
            size={isMobile ? 'xLargeMobile' : 'small'}
          >
            {copyConfig(t)[copyStatus].icon}
            {copyStatus != 'idle' && (
              <p
                className={typographyVariants({
                  color: copyStatus,
                  variant: 'h5',
                })}
              >
                {copyConfig(t)[copyStatus].text}
              </p>
            )}
          </Button>
        </TooltipTrigger>
        <TooltipContent
          className="pointer-events-none"
          sideOffset={10}
          side="top"
          align="center"
          arrow={false}
        >
          {t('Copy')}
        </TooltipContent>
      </Tooltip>
    </div>
  );
};

export { AiToolbar };
